import React from 'react';
import { InfoCircleOutlined } from '@ant-design/icons';
import { Alert, Collapse, Popover, Tag, Typography } from 'antd';
import ReactMarkdown from 'react-markdown';
import rehypeKatex from 'rehype-katex';
import remarkMath from 'remark-math';
import 'katex/dist/katex.min.css';

type InfoPointProps = {
  title: string;
  children: React.ReactNode;
};

type InfoLabelProps = InfoPointProps & {
  label: React.ReactNode;
};

const MODEL_STEPS = [
  {
    title: 'Issue the bond',
    body: "Choose a date and starting tenor. The ask side of that day's last complete benchmark yield pair becomes the fixed coupon; issuance is rejected if that pair is crossed.",
  },
  {
    title: 'Age its cash flows',
    body: 'Face value, coupon, maturity, and semiannual payment dates stay fixed. Residual maturity decreases continuously.',
  },
  {
    title: 'Build the moving yield',
    body: 'Bracket residual maturity with the maintained 2Y, 3Y, 5Y, 7Y, and 10Y nodes, then interpolate bid and ask yields independently.',
  },
  {
    title: 'Reprice over time',
    body: 'Discount each side of the remaining cash flows, derive accrued interest, and remove it to produce clean bid and ask quotes.',
  },
];

const TENOR_MAP = [
  ['2Y', 'US02YT'],
  ['3Y', 'US03YT'],
  ['5Y', 'US05YT'],
  ['7Y', 'US07YT'],
  ['10Y', 'US10YT'],
];

const PRICING_MATH = String.raw`
### 1. Residual maturity

Let $m=2$ coupon payments per year, $N(t)$ be the number of remaining coupons,
and $u(t)$ be the fraction of one coupon period from valuation time $t$ to the
next coupon. The continuously declining residual maturity is

$$
R(t)=\frac{u(t)+N(t)-1}{m}.
$$

### 2. Interpolated bid and ask yields

For the maintained tenors $T_L \le R(t) \le T_U$, define

$$
w(t)=\frac{R(t)-T_L}{T_U-T_L},
\qquad
y_s(t)=(1-w(t))y_{L,s}(t)+w(t)y_{U,s}(t),
\quad s\in\{bid,ask\}.
$$

At an exact maintained tenor, the model uses that node directly. Each source
yield is the latest available bid or ask yield for its tenor at that valuation
timestamp. All yields in these formulas are annual rates expressed as decimals;
the interface displays them as percentages.

### 3. Dirty bid and ask prices

Let $F=100$, $c$ be the fixed annual coupon rate expressed as a decimal, and
$C=Fc/m$ be one coupon payment. The dirty price is

$$
P_s^{dirty}(t)=
\sum_{j=0}^{N(t)-2}
\frac{C}{\left(1+\frac{y_s(t)}{m}\right)^{u(t)+j}}
+
\frac{F+C}{\left(1+\frac{y_s(t)}{m}\right)^{u(t)+N(t)-1}}.
$$

The final term combines principal with the final coupon. Because valuation can
fall between coupon dates, the fractional first exponent makes this a dirty
price calculation.

### 4. Accrued interest and clean prices

Let $a(t)$ be elapsed time since the previous coupon and $d(t)$ be the full
coupon-period length, measured consistently. Accrued interest in price points is

$$
AI(t)=C\frac{a(t)}{d(t)}.
$$

The clean quotes consumed by PM's bond trading mechanics are then

$$
P_s^{clean}(t)=P_s^{dirty}(t)-AI(t),
\quad s\in\{bid,ask\}.
$$
`;

export const InfoPoint: React.FC<InfoPointProps> = ({ title, children }) => (
  <Popover
    content={<div className="bonds-playground__info-content">{children}</div>}
    placement="top"
    title={title}
    trigger={['hover', 'click']}
  >
    <button
      aria-label={`About ${title}`}
      className="bonds-playground__info-trigger"
      type="button"
    >
      <InfoCircleOutlined />
    </button>
  </Popover>
);

export const InfoLabel: React.FC<InfoLabelProps> = ({
  label,
  title,
  children,
}) => (
  <span className="bonds-playground__info-label">
    {label}
    <InfoPoint title={title}>{children}</InfoPoint>
  </span>
);

export const ModelOverview: React.FC = () => (
  <section className="bonds-playground__model-guide">
    <div className="bonds-playground__model-guide-heading">
      <div>
        <Tag color="blue">V1 concept-validation model</Tag>
        <Tag>Calculated in memory · not persisted</Tag>
        <Typography.Title level={4}>
          How the synthetic bond works
        </Typography.Title>
      </div>
      <InfoPoint title="What this playground creates">
        The API calculates an in-memory synthetic bond response for inspection.
        It does not save or issue a tradeable instrument.
      </InfoPoint>
    </div>

    <Typography.Paragraph type="secondary">
      This page tests whether a fixed-coupon bond can be aged and repriced from
      a deliberately small set of liquid benchmark yield tenors.
    </Typography.Paragraph>

    <div className="bonds-playground__model-flow" role="list">
      {MODEL_STEPS.map((step, index) => (
        <div
          className="bonds-playground__model-step"
          key={step.title}
          role="listitem"
        >
          <span className="bonds-playground__model-step-number">
            {index + 1}
          </span>
          <div>
            <Typography.Text strong>{step.title}</Typography.Text>
            <Typography.Paragraph>{step.body}</Typography.Paragraph>
          </div>
        </div>
      ))}
    </div>

    <div className="bonds-playground__tenor-map">
      <Typography.Text strong>Maintained yield inputs</Typography.Text>
      <div className="bonds-playground__tenor-tags">
        {TENOR_MAP.map(([tenor, asset]) => (
          <Tag key={tenor}>
            {tenor} · {asset}
          </Tag>
        ))}
      </div>
    </div>

    <Alert
      description="This version interpolates bid- and ask-side yields-to-maturity and applies each side's yield to every remaining cash flow. It is intentionally not a bootstrapped spot/discount-curve valuation."
      message="Important interpretation boundary"
      showIcon
      type="warning"
    />
  </section>
);

export const PricingModelExplanation: React.FC = () => (
  <section className="bonds-playground__calculation-guide">
    <Collapse
      items={[
        {
          key: 'calculation',
          label: (
            <span className="bonds-playground__calculation-label">
              Calculation formulas and definitions
              <Tag color="geekblue">KaTeX</Tag>
            </span>
          ),
          children: (
            <div className="bonds-playground__math">
              <ReactMarkdown
                rehypePlugins={[rehypeKatex]}
                remarkPlugins={[remarkMath]}
              >
                {PRICING_MATH}
              </ReactMarkdown>
            </div>
          ),
        },
        {
          key: 'rules',
          label: 'V1 data and calculation rules',
          children: (
            <ul className="bonds-playground__rules-list">
              <li>
                The coupon is the ask side of the last complete bid/ask yield
                row on the issue date. Issuance is rejected if that pair is
                crossed.
              </li>
              <li>
                Each bid/ask curve-node pair comes from one market-history row.
                Only DAILY rows are used; valid pairs are carried forward
                without a V1 staleness limit.
              </li>
              <li>
                A new price point is calculated whenever any maintained curve
                node receives a valid paired update and at every coupon boundary
                within the available DAILY data horizon. No points are projected
                beyond the final system observation.
              </li>
              <li>
                Coupon dates are placed every six calendar months from the issue
                timestamp, with no business-day adjustment. The fraction to the
                next coupon uses elapsed timestamp seconds within that period.
              </li>
              <li>
                No extrapolation is performed below 2Y. The series ends when
                residual maturity crosses below two years.
              </li>
              <li>
                A valuation snapshot is skipped if either required bounding
                yield has no available observation.
              </li>
              <li>
                Dirty bid and ask values are discounted independently. Accrued
                interest is then removed from both to create the clean quotes
                expected by PM's bond settlement mechanics.
              </li>
              <li>
                Bid yield must be at least ask yield, and both dirty and clean
                bid prices must not exceed ask. Crossed observations are never
                silently swapped.
              </li>
            </ul>
          ),
        },
      ]}
    />
  </section>
);
